import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

interface Slide {
  id: string;
  number: number;
  title: string;
  summary: string;
  cover: string;
  airDate: string;
}

export default function HeroCarousel({ slides }: { slides: Slide[] }) {
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);

  const goTo = useCallback(
    (index: number) => {
      setCurrent((index + slides.length) % slides.length);
    },
    [slides.length]
  );

  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  useEffect(() => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(() => {
      setCurrent((c) => (c + 1) % slides.length);
    }, 6000);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [slides.length]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      if (delta < 0) next();
      else prev();
    }
    touchStartX.current = null;
  };

  return (
    <section
      className="relative w-full overflow-hidden bg-secondary-900"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="relative h-[440px] md:h-[600px] w-full">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === current ? "opacity-100 z-10" : "opacity-0 z-0"
            }`}
          >
            <img
              src={slide.cover}
              alt={`公公与儿媳 第${slide.number}集 ${slide.title} 剧集封面`}
              title={`公公与儿媳 第${slide.number}集 ${slide.title}`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />

            <div className="absolute inset-0 flex items-center">
              <div className="w-full px-6 md:px-12 lg:px-16">
                <div className="max-w-2xl">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-3 py-1 rounded-full bg-accent-500 text-white text-xs font-medium">
                      焦点推荐
                    </span>
                    <span className="text-xs md:text-sm text-white/80">
                      第 {slide.number} 集 · {slide.airDate}
                    </span>
                  </div>
                  <h1 className="font-heading font-black text-2xl md:text-4xl lg:text-5xl text-white leading-tight mb-4">
                    第{slide.number}集 · {slide.title}
                  </h1>
                  <p className="text-sm md:text-base text-white/85 leading-relaxed mb-6 max-w-xl line-clamp-2">
                    {slide.summary}
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    <Link
                      to={`/episode/${slide.id}`}
                      className="inline-flex items-center gap-2 px-5 md:px-6 py-2.5 md:py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-sm md:text-base font-medium whitespace-nowrap transition-colors cursor-pointer"
                    >
                      <i className="ri-play-circle-line text-lg" />
                      查看本集剧情
                    </Link>
                    <Link
                      to="/episodes"
                      className="inline-flex items-center gap-2 px-5 md:px-6 py-2.5 md:py-3 rounded-md bg-white/10 hover:bg-white/20 text-white text-sm md:text-base font-medium whitespace-nowrap transition-colors cursor-pointer border border-white/20"
                    >
                      <i className="ri-list-check-2 text-lg" />
                      全部剧集
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        <button
          type="button"
          aria-label="上一张"
          onClick={prev}
          className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-white/15 hover:bg-white/30 text-white backdrop-blur-sm transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-2xl" />
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={next}
          className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-white/15 hover:bg-white/30 text-white backdrop-blur-sm transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-2xl" />
        </button>

        <div className="absolute bottom-6 left-0 right-0 z-20 flex items-center justify-center gap-2">
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              aria-label={`切换到第 ${index + 1} 张`}
              onClick={() => goTo(index)}
              className={`h-2 rounded-full transition-all cursor-pointer ${
                index === current ? "w-8 bg-accent-500" : "w-2 bg-white/50 hover:bg-white/80"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}